import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { formatLondonDateTime } from '../shared/dateUtils';
import Modal from './Modal';
import './DiscountManagement.css';

interface DynamicDiscount {
    code: string;
    percentage: number;
    fixedAmount?: number;        // Fixed £ discount applied before percentage
    minOrderOverride?: number;   // Override global minimum order amount (can be 0)
    maxUses: number;
    usedCount: number;
    message: string;
    adminNote: string;
    createdAt: string;
    createdBy: string;
    expiresAt?: string;          // Expiry date/time
}

interface DiscountFormData {
    code: string;
    percentage: number;
    fixedAmount: string;         // String for form input, parsed to number
    minOrderOverride: string;    // String for form input, parsed to number or undefined
    maxUses: number;
    message: string;
    adminNote: string;
    expiresAt: string;           // Date/time string for form input
}

const DiscountManagement: React.FC = () => {
    const [discounts, setDiscounts] = useState<DynamicDiscount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [codeCheckError, setCodeCheckError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingDiscount, setEditingDiscount] = useState<DynamicDiscount | null>(null);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    const [formData, setFormData] = useState<DiscountFormData>({
        code: 'DF',
        percentage: 0,
        fixedAmount: '',
        minOrderOverride: '',
        maxUses: 1,
        message: 'This discount code is valid for one-time use only and applies exclusively to orders placed to [number & street].',
        adminNote: '',
        expiresAt: '',
    });

    const fetchDiscounts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiService.getDynamicDiscounts();
            setDiscounts(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load discounts');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDiscounts();
    }, [fetchDiscounts]);

    const handleCreate = () => {
        setEditingDiscount(null);
        setFormData({
            code: 'DF',
            percentage: 0,
            fixedAmount: '',
            minOrderOverride: '',
            maxUses: 1,
            message: 'This discount code is valid for one-time use only and applies exclusively to orders placed to [number & street].',
            adminNote: '',
            expiresAt: '',
        });
        setIsFormOpen(true);
        setSubmitError(null);
        setCodeCheckError(null);
    };

    const handleEdit = (discount: DynamicDiscount) => {
        setEditingDiscount(discount);
        setFormData({
            code: discount.code,
            percentage: discount.percentage,
            fixedAmount: discount.fixedAmount?.toString() || '',
            minOrderOverride: discount.minOrderOverride?.toString() || '',
            maxUses: discount.maxUses,
            message: discount.message,
            adminNote: discount.adminNote,
            expiresAt: discount.expiresAt ? discount.expiresAt.slice(0, 16) : '',
        });
        setIsFormOpen(true);
        setSubmitError(null);
        setCodeCheckError(null);
    };

    const handleCopyCode = async (code: string) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(null), 2000);
        } catch (err) {
            console.error('Failed to copy code:', err);
        }
    };

    const handleDelete = async (code: string) => {
        if (!confirm(`Are you sure you want to delete discount code "${code}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await apiService.deleteDynamicDiscount(code);
            setSuccessMessage(`Discount code "${code}" deleted successfully!`);
            setTimeout(() => setSuccessMessage(null), 3000);
            fetchDiscounts();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete discount');
        }
    };

    const handleCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.toUpperCase();

        // Ensure code always starts with DF
        if (!value.startsWith('DF')) {
            value = 'DF' + value.replace(/^DF/i, '');
        }

        setFormData({ ...formData, code: value });
        setCodeCheckError(null);

        // Check if code already exists (debounced check on blur or submit)
    };

    const handleCodeBlur = async () => {
        if (formData.code.length > 2) {
            try {
                const result = await apiService.checkDynamicDiscountExists(formData.code);
                if (result.exists) {
                    setCodeCheckError(`Code "${formData.code}" already exists. Please choose a different code.`);
                }
            } catch (err) {
                // Ignore check errors
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.code || formData.code.length <= 2) {
            setSubmitError('Please enter a valid discount code (must be more than just "DF")');
            return;
        }

        if (!formData.code.startsWith('DF')) {
            setSubmitError('Discount code must start with "DF"');
            return;
        }

        // Parse fixedAmount - empty string means undefined
        const parsedFixedAmount = formData.fixedAmount.trim()
            ? parseFloat(formData.fixedAmount)
            : undefined;

        // Validate fixedAmount if provided
        if (parsedFixedAmount !== undefined && (!Number.isFinite(parsedFixedAmount) || parsedFixedAmount < 0)) {
            setSubmitError('Fixed amount must be a valid positive number');
            return;
        }

        // Must have at least fixedAmount or percentage > 0
        if ((!parsedFixedAmount || parsedFixedAmount <= 0) && formData.percentage <= 0) {
            setSubmitError('Must specify at least a fixed amount or percentage discount');
            return;
        }

        // Percentage can be 0 (for fixed-amount-only discounts), just validate range
        if (formData.percentage < 0 || formData.percentage > 100) {
            setSubmitError('Percentage must be between 0 and 100');
            return;
        }

        // Parse minOrderOverride - empty string means undefined (use global default)
        const parsedMinOrderOverride = formData.minOrderOverride.trim()
            ? parseFloat(formData.minOrderOverride)
            : undefined;

        // Validate minOrderOverride if provided (can be 0)
        if (parsedMinOrderOverride !== undefined && (!Number.isFinite(parsedMinOrderOverride) || parsedMinOrderOverride < 0)) {
            setSubmitError('Minimum order override must be a valid number (0 or greater)');
            return;
        }

        if (formData.maxUses < 1) {
            setSubmitError('Max uses must be at least 1');
            return;
        }

        // Parse expiresAt - empty string means undefined
        const parsedExpiresAt = formData.expiresAt.trim()
            ? new Date(formData.expiresAt).toISOString()
            : undefined;

        try {
            setIsSubmitting(true);
            setSubmitError(null);

            // If editing, delete the old discount first
            if (editingDiscount) {
                await apiService.deleteDynamicDiscount(editingDiscount.code);
            }

            await apiService.createDynamicDiscount({
                code: formData.code,
                percentage: formData.percentage,
                fixedAmount: parsedFixedAmount,
                minOrderOverride: parsedMinOrderOverride,
                maxUses: formData.maxUses,
                message: formData.message,
                adminNote: formData.adminNote,
                expiresAt: parsedExpiresAt,
            });

            setSuccessMessage(`Discount code "${formData.code}" ${editingDiscount ? 'updated' : 'created'} successfully!`);
            setTimeout(() => setSuccessMessage(null), 3000);
            setIsFormOpen(false);
            setEditingDiscount(null);
            fetchDiscounts();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create discount';
            if (errorMessage.includes('already exists')) {
                setCodeCheckError(errorMessage);
            } else {
                setSubmitError(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setIsFormOpen(false);
        setEditingDiscount(null);
        setSubmitError(null);
        setCodeCheckError(null);
    };

    const formatDate = (dateString: string) => {
        return formatLondonDateTime(dateString);
    };

    const getUsageStatus = (discount: DynamicDiscount) => {
        const remaining = discount.maxUses - discount.usedCount;
        if (remaining <= 0) {
            return <span className="usage-badge exhausted">Exhausted</span>;
        }
        if (remaining === 1 && discount.maxUses > 1) {
            return <span className="usage-badge low">1 left</span>;
        }
        return <span className="usage-badge available">{remaining} left</span>;
    };

    // Filter discounts based on search query (prefix match on code or admin note)
    const filteredDiscounts = discounts.filter(discount => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toUpperCase().trim();
        return discount.code.toUpperCase().startsWith(query) ||
            discount.adminNote.toUpperCase().includes(query);
    });

    if (loading) {
        return (
            <div className="discount-management">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading discount codes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="discount-management">
                <div className="error-container">
                    <h2>Error Loading Discounts</h2>
                    <p>{error}</p>
                    <button onClick={fetchDiscounts} className="button primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="discount-management">
            <div className="discount-header">
                <div>
                    <h1>🎟️ Dynamic Discount Codes</h1>
                    <p className="subtitle">Manage one-time and limited-use discount codes (DF prefix)</p>
                </div>
                <button onClick={handleCreate} className="button primary">
                    ➕ Create Discount Code
                </button>
            </div>

            {successMessage && (
                <div className="success-message">
                    <span className="success-icon">✅</span>
                    {successMessage}
                </div>
            )}

            {/* Search Box */}
            {discounts.length > 0 && (
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Search by code prefix or admin note (e.g., DF12AB)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                    {searchQuery && (
                        <span className="search-results-count">
                            {filteredDiscounts.length} of {discounts.length} codes
                        </span>
                    )}
                </div>
            )}

            {!discounts || discounts.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🎟️</div>
                    <h3>No Dynamic Discount Codes Yet</h3>
                    <p>Create your first one-time discount code to share with customers</p>
                    <button onClick={handleCreate} className="button primary">
                        ➕ Create Discount Code
                    </button>
                </div>
            ) : (
                <div className="discount-table-container">
                    <table className="discount-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Discount</th>
                                <th>Min Order</th>
                                <th>Usage</th>
                                <th>Expires</th>
                                <th>Admin Note</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDiscounts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="no-results">
                                        No discount codes match "{searchQuery}"
                                    </td>
                                </tr>
                            ) : filteredDiscounts.map((discount) => {
                                // Format discount description
                                const discountParts: string[] = [];
                                if (discount.fixedAmount && discount.fixedAmount > 0) {
                                    discountParts.push(`£${discount.fixedAmount.toFixed(2)}`);
                                }
                                if (discount.percentage && discount.percentage > 0) {
                                    discountParts.push(`${discount.percentage}%`);
                                }
                                const discountText = discountParts.join(' + ') || '-';

                                return (
                                    <tr key={discount.code} className={discount.usedCount >= discount.maxUses ? 'exhausted-row' : ''}>
                                        <td className="discount-code">
                                            <button
                                                className={`code-copy-btn ${copiedCode === discount.code ? 'copied' : ''}`}
                                                onClick={() => handleCopyCode(discount.code)}
                                                title="Click to copy code"
                                            >
                                                <strong>{discount.code}</strong>
                                                <span className="copy-indicator">
                                                    {copiedCode === discount.code ? '✓' : '📋'}
                                                </span>
                                            </button>
                                        </td>
                                        <td className="discount-percentage">
                                            <span className="percentage-badge">{discountText}</span>
                                        </td>
                                        <td className="discount-min-order">
                                            {discount.minOrderOverride !== undefined ? (
                                                <span className="min-order-badge">
                                                    {discount.minOrderOverride === 0 ? '£0 (free)' : `£${discount.minOrderOverride.toFixed(2)}`}
                                                </span>
                                            ) : (
                                                <span className="text-muted">default</span>
                                            )}
                                        </td>
                                        <td className="discount-usage">
                                            <div className="usage-info">
                                                <span>{discount.usedCount} / {discount.maxUses}</span>
                                                {getUsageStatus(discount)}
                                            </div>
                                        </td>
                                        <td className="discount-date">
                                            {discount.expiresAt ? formatDate(discount.expiresAt) : <span className="text-muted">never</span>}
                                        </td>
                                        <td className="discount-admin-note">
                                            {discount.adminNote ? (
                                                <span className="admin-note-text">{discount.adminNote}</span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td className="discount-actions">
                                            <button
                                                onClick={() => handleEdit(discount)}
                                                className="action-button edit"
                                                title="Edit"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDelete(discount.code)}
                                                className="action-button delete"
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={isFormOpen}
                onClose={handleCancel}
                showCloseButton={true}
            >
                <div className="discount-form-container">
                    <h2>{editingDiscount ? 'Edit Discount Code' : 'Create Discount Code'}</h2>
                    <p className="form-description">
                        {editingDiscount
                            ? 'Update this discount code. All codes must start with "DF".'
                            : 'Create a one-time or limited-use discount code. All codes must start with "DF".'
                        }
                    </p>

                    <form onSubmit={handleSubmit} className="discount-form">
                        <div className="form-group">
                            <label htmlFor="code">Discount Code *</label>
                            <input
                                type="text"
                                id="code"
                                value={formData.code}
                                onChange={handleCodeChange}
                                onBlur={handleCodeBlur}
                                placeholder="DF12AB1"
                                className={codeCheckError ? 'input-error' : ''}
                                required
                            />
                            <small className="help-text">
                                Must start with "DF". Example: DF12AB1 (12=door number, AB=initials, 1=sequence)
                            </small>
                            {codeCheckError && (
                                <div className="field-error">{codeCheckError}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="fixedAmount">Fixed Amount (£)</label>
                                <input
                                    type="number"
                                    id="fixedAmount"
                                    value={formData.fixedAmount}
                                    onChange={(e) => setFormData({ ...formData, fixedAmount: e.target.value })}
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g., 7.99"
                                />
                                <small className="help-text">£ discount applied first (e.g., free burger value)</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="percentage">Then Percentage (%)</label>
                                <input
                                    type="number"
                                    id="percentage"
                                    value={formData.percentage}
                                    onChange={(e) => setFormData({ ...formData, percentage: parseInt(e.target.value) || 0 })}
                                    min="0"
                                    max="100"
                                />
                                <small className="help-text">0-100% applied on remainder</small>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="minOrderOverride">Min Order Override (£)</label>
                                <input
                                    type="number"
                                    id="minOrderOverride"
                                    value={formData.minOrderOverride}
                                    onChange={(e) => setFormData({ ...formData, minOrderOverride: e.target.value })}
                                    min="0"
                                    step="0.01"
                                    placeholder="Leave empty for default"
                                />
                                <small className="help-text">Set to 0 to allow free orders</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="maxUses">Max Uses *</label>
                                <input
                                    type="number"
                                    id="maxUses"
                                    value={formData.maxUses}
                                    onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) || 1 })}
                                    min="1"
                                    required
                                />
                                <small className="help-text">How many times can this code be used?</small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="expiresAt">Expires At</label>
                            <input
                                type="datetime-local"
                                id="expiresAt"
                                value={formData.expiresAt}
                                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                            />
                            <small className="help-text">Leave empty for no expiry</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">User Message</label>
                            <textarea
                                id="message"
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                placeholder="Message shown to the customer when they apply this code..."
                                rows={3}
                            />
                            <small className="help-text">This message will be displayed to the customer</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="adminNote">Admin Note</label>
                            <textarea
                                id="adminNote"
                                value={formData.adminNote}
                                onChange={(e) => setFormData({ ...formData, adminNote: e.target.value })}
                                placeholder="Internal note (e.g., 'John Doe, 12 Main Street')"
                                rows={2}
                            />
                            <small className="help-text">Internal reminder - not shown to customers</small>
                        </div>

                        {submitError && (
                            <div className="error-message">
                                <span className="error-icon">❌</span>
                                {submitError}
                            </div>
                        )}

                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="button secondary">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="button primary"
                                disabled={isSubmitting || !!codeCheckError}
                            >
                                {isSubmitting
                                    ? (editingDiscount ? 'Saving...' : 'Creating...')
                                    : (editingDiscount ? 'Save Changes' : 'Create Discount Code')
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default DiscountManagement;
